/**
 * Host-page navigation for action buttons and deep links.
 *
 * The widget is embedded on whoever is hosting it. Absolute URLs that point
 * at the application's configured origin (or a custom-scheme deep link)
 * must still land on this page: same origin, path, query and hash.
 */

import type { NavigationAction } from './types';

const HTTP = new Set(['http:', 'https:']);
const HTTP_OR_PATH = /^(https?:|\/|#|\?)/i;

function firstHref(...values: Array<string | undefined>): string | null {
    for (const value of values) {
        if (typeof value === 'string' && value.trim() !== '') {
            return value.trim();
        }
    }
    return null;
}

export function pickNavigationHref(
    action: NavigationAction,
    location: Pick<Location, 'origin' | 'protocol'>
): string | null {
    const inBrowser = HTTP.has(location.protocol);
    const raw = inBrowser
        ? firstHref(
              action.webUrl,
              HTTP_OR_PATH.test(action.url) ? action.url : undefined
          )
        : firstHref(action.deepLink, action.url);

    if (raw === null) {
        return null;
    }

    if (/^(mailto:|tel:)/i.test(raw)) {
        return raw;
    }
    if (/^[a-z][a-z0-9+.-]*:/i.test(raw) && !/^https?:/i.test(raw)) {
        if (inBrowser) {
            const web = firstHref(action.webUrl);
            return web === null ? null : rewriteToOrigin(web, location.origin);
        }
        return raw;
    }
    return rewriteToOrigin(raw, location.origin);
}

export function rewriteToOrigin(url: string, origin: string): string {
    if (url.startsWith('#') || url.startsWith('/') || url.startsWith('?')) {
        const path = url.startsWith('#') ? `/${url}` : url;
        return new URL(path, origin).href;
    }
    try {
        const parsed = new URL(url);
        if (HTTP.has(parsed.protocol)) {
            return `${origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
        }
    } catch {
        return new URL(url, origin).href;
    }
    return new URL(url, origin).href;
}

export function navigateOnHost(action: NavigationAction): void {
    const href = pickNavigationHref(action, window.location);
    if (href === null) {
        return;
    }
    if (/^(mailto:|tel:)/i.test(href)) {
        window.location.assign(href);
        return;
    }

    const current = window.location;
    const target = new URL(href, current.href);
    if (target.origin !== current.origin) {
        window.location.assign(target.href);
        return;
    }

    // Same-origin path, query and hash changes stay in this document so the
    // chatbot is not torn down and the conversation is not reset.
    const next = `${target.pathname}${target.search}${target.hash}`;
    const here = `${current.pathname}${current.search}${current.hash}`;
    if (here !== next) {
        window.history.pushState({}, '', next);
    }
    window.dispatchEvent(new PopStateEvent('popstate'));
    if (target.hash !== '' && target.hash !== current.hash) {
        window.dispatchEvent(new HashChangeEvent('hashchange'));
    }
    scrollToHash(target.hash);
}

/** True when the widget can change URL without unloading the host page. */
export function isInPlaceNavigation(
    currentOrigin: string,
    targetOrigin: string
): boolean {
    return currentOrigin === targetOrigin;
}

function scrollToHash(hash: string): void {
    const id = decodeURIComponent(hash.replace(/^#/, ''));
    if (id === '') {
        return;
    }
    const escaped =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
            ? CSS.escape(id)
            : id.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const el =
        document.getElementById(id) ??
        document.querySelector(`[name="${escaped}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
